import clsx from 'clsx';
import { useState } from 'react';

import EditableField from '~components/generic/editable-field/EditableField/EditableField';
import AddLink from '~components/generic/elements/add-link/AddLink';
import Button from '~components/generic/elements/Button';
import DraggableModuleContainer from '~components/generic/module/DraggableModuleContainer';
import { ModulesListPopover } from '~components/generic/module/ModulesListPopover/ModulesListPopover';
import { IEntityComponentProps } from '~interfaces/props';
import { getRelatedEntityIds } from '~services/entity-helpers';
import { removeItemFromPrimitiveArray } from '~services/immutable';
import { getModuleUrlIdFromData } from '~services/location-helpers';
import { getLastItem } from '~utils/array';

export default function ModuleWizard(props: IEntityComponentProps) {
  const [activeLevelId, setActiveLevelId] = useState<string | null>(null);
  const [previousLevelIds, setPreviousLevelIds] = useState<string[]>([]);

  function handleChangeLevel(id: string) {
    setActiveLevelId(id);
    setPreviousLevelIds(curr => {
      if (!id) {
        return [];
      }
      return [...curr, activeLevelId];
    });
  }

  /**
   * Go one level back up by removing the last element in the activeLevelPath
   */
  function handleLevelBack() {
    const previousLevelId = getLastItem(previousLevelIds);
    setActiveLevelId(previousLevelId);
    setPreviousLevelIds(curr => removeItemFromPrimitiveArray(curr, previousLevelId));
  }

  const { data, className, editing, onChange, onAddNewChild, origData, stable } = props;
  if (!data) {
    return null;
  }
  const content = data.attributes;
  const childModuleIds = getRelatedEntityIds(data, 'field_modules');
  const childModuleIdsLength = childModuleIds.length - 1;
  const levelId = getRelatedEntityIds(data, 'field_level')[0];
  const moduleClasses = clsx(
    { ...className.module },
    'c-wizard c-module o-container o-container--lg u-padding-vertical-md',
  );
  const buttonClasses = 'c-button u-margin-bottom-md';
  const buttonClassesSec = 'c-button c-button--dark u-margin-bottom-md';
  const buttonClassesThird = 'c-button c-button--dark-transparent u-margin-bottom-md u-float-right';

  return (
    <div className={moduleClasses} id={getModuleUrlIdFromData(data.id, data.type)}>
      <div className={clsx(className?.container)}>
        <div className="o-grid o-grid--unpad o-grid--wrap c-module__header">
          <div className="o-grid__cell o-grid__cell--width-12">
            <EditableField
              hide={content.field_hide_title}
              entityType={data.type}
              fieldName="attributes.field_title.value"
              data={content.field_title.value}
              editing={editing}
              onChange={onChange}
            />
          </div>
        </div>

        <div className="c-wizard__content u-margin-bottom-lg u-margin-top-sm c-module__content">
          {editing && childModuleIdsLength === -1 && (
            <div className="u-padding-bottom-xl container relative">
              <ModulesListPopover type={data.type} parentContext={data.id} index={0} />
            </div>
          )}
          {activeLevelId === null && (
            <div className="c-wizard__start">
              {childModuleIds &&
                childModuleIds.map((id, i) => {
                  return (
                    <div key={i} className="">
                      {editing && (
                        <div className="container relative">
                          <ModulesListPopover type={data.type} parentContext={data.id} index={i} />
                        </div>
                      )}
                      <DraggableModuleContainer
                        parentIsStable={stable}
                        index={i}
                        key={id}
                        entityId={id}
                        parentContext={data.id}
                        parentType={data.type}
                        fieldName="relationships.field_modules.data"
                        siblingsLength={childModuleIdsLength}
                        canMove={editing}
                      />
                    </div>
                  );
                })}
              {levelId && (
                <Button label="Start" className={buttonClassesSec} onClick={() => handleChangeLevel(levelId)} />
              )}
              {!levelId && editing && (
                <AddLink
                  onClick={() =>
                    onAddNewChild({
                      id: origData,
                      fieldName: 'relationships.field_level.data',
                      templateType: 'paragraph--level',
                    })
                  }
                />
              )}
            </div>
          )}
          {activeLevelId !== null ? (
            <div className="c-wizard__level">
              {data.relationships.field_level && (
                <DraggableModuleContainer
                  parentIsStable={stable}
                  index={0}
                  key={activeLevelId}
                  entityId={activeLevelId}
                  parentContext={data.id}
                  parentType={data.type}
                  fieldName="relationships.field_level.data"
                  siblingsLength={0}
                  canMove={editing}
                  onClick={handleChangeLevel}
                  activeId={activeLevelId}
                  parentId={levelId}
                />
              )}
            </div>
          ) : null}
        </div>

        {activeLevelId ? (
          <div className="o-grid__cell--width-12">
            <Button label="Back" className={buttonClasses} onClick={handleLevelBack} />
            <Button label="Restart" className={buttonClassesThird} onClick={() => handleChangeLevel(null)} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
